import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  PermissionsProvider,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import {appPaths} from '@/config/appPaths';
import {useCountryList} from '@/context/CountryListContext';
import type {AfriexCountry, AfriexCurrencyCode} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';

import {
  DetailRow,
  ExpandButton,
  ExpandedPanel,
  NotSet,
  PanelActions,
  PanelColumns,
  PanelSection,
  PillRow,
  StatePill,
} from '../ExpandedRowPanel';
import {
  COUNTRY_AVAILABILITY_PERMISSIONS,
  resolvePermission,
} from './countryAvailability';
import {formatKycProcessor} from './kycProcessorLabels';

type SettingsCountryTableProps = {
  list: AfriexCountry[];
};

/**
 * The KYC provider that used to have its own column now lives in the expanded
 * panel beside the referral terms it is read with, so the flat row stays
 * scannable: name, code, status.
 */
export default function SettingsCountryTable({
  list,
}: SettingsCountryTableProps) {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-12 px-4">&nbsp;</Th>
          <Th className="py-4 px-2 w-24">Name</Th>
          <Th className="py-4 px-2 w-24">Country Code</Th>
          <Th className="py-4 px-2 w-24">Status</Th>
        </Tr>
      </Thead>

      <TBody className="bg-white divide-y divide-gray-100">
        {list.map((item: AfriexCountry, index: number) => (
          <SettingsCountryRow
            key={item?.id ?? item?.iso2 ?? index}
            item={item}
            index={index}
          />
        ))}
      </TBody>
    </Table>
  );
}

const SettingsCountryRow = ({
  item,
  index,
}: {
  item: AfriexCountry;
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;

  const countryCode = item?.iso2?.toUpperCase() as AfriexCurrencyCode;
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric className="px-4">
          <ExpandButton
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
            label={item?.name}
          />
        </Td>
        <Td className={'font-medium'}>{item?.name}</Td>
        <Td>
          {countryCode} {mapCountryCodeToFlags(countryList)[countryCode]}
        </Td>
        <Td>{item.status}</Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={4} className="p-0 m-0">
            <ExpandedPanel>
              <CountryDetailPanel data={item} />
            </ExpandedPanel>
          </Td>
        </Tr>
      )}
    </>
  );
};

/**
 * Everything the Manage page shows read-only, in one place.
 *
 * The referral terms mirror `ReferralSettings.tsx`, the processor mirrors
 * `KycSettings.tsx`, and the permission pills mirror `AvailabilitySettings.tsx`;
 * all three are fetched with the list now, so opening a row does not cost a
 * round-trip.
 *
 * The pills show the two permissions that have a consumer, and show them
 * RESOLVED rather than raw. `StatePill`'s grey "unset" state carries the
 * tooltip "not configured, which counts as allowed", true of `canWithdraw`,
 * read as `!== false`, and the exact opposite of `canSend`, read as
 * `?? false`. Passing a resolved boolean keeps that tooltip from ever firing
 * and makes an unconfigured `canSend` read as what it is: no payouts. The
 * Availability tab is where the stored value is distinguished from its
 * default, because that is where someone is about to change it.
 */
const CountryDetailPanel = ({data}: {data: AfriexCountry}) => {
  const referral = data?.params?.referralParams;
  const permissions = data?.permissions;

  return (
    <div className="w-full">
      <PanelColumns>
        <div>
          <PanelSection title="KYC">
            <DetailRow
              label="Processor"
              value={
                data?.params?.kycProcessor ? (
                  formatKycProcessor(data.params.kycProcessor)
                ) : (
                  <NotSet>Not set (uses global default)</NotSet>
                )
              }
            />
          </PanelSection>

          <PanelSection title="Referral settings" className="mt-5">
            <DetailRow
              label="Threshold (days)"
              value={referral?.cumulativeThreshold ?? <NotSet />}
            />
            <DetailRow
              label="Bonus per referrer"
              value={referral?.bonusAmountPerReferrer ?? <NotSet />}
            />
            <DetailRow
              label="Bonus per referee"
              value={referral?.bonusAmountPerReferee ?? <NotSet />}
            />
          </PanelSection>
        </div>

        <PanelSection title="App permissions">
          <PillRow>
            {COUNTRY_AVAILABILITY_PERMISSIONS.map(
              ({key, label, defaultWhenUnset}) => (
                <StatePill
                  key={key}
                  label={label}
                  allowed={resolvePermission(
                    permissions,
                    key,
                    defaultWhenUnset,
                  )}
                />
              ),
            )}
          </PillRow>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
            Enforced by the app, not the server: these hide payout options
            rather than rejecting transactions. To stop money moving, use
            Corridor Availability on the Currencies tab. An unconfigured
            permission is shown as the default the app applies, which differs
            per permission; the Availability tab spells out which are actually
            set.
          </p>
        </PanelSection>
      </PanelColumns>

      <PermissionsProvider permission={AfriexAdminPermission.CountryUpdate}>
        <PanelActions>
          <Button
            variant="solid"
            onClick={() =>
              window.open(
                `../${appPaths.config}/country/${data?.iso2}`,
                '_self',
              )
            }
          >
            Manage country
          </Button>
        </PanelActions>
      </PermissionsProvider>
    </div>
  );
};
